/**
 * Shared CASTABILITY predicate (#786 §3) — the ONE client-side answer to "can
 * I cast this right now, and if not, why?".
 *
 * WHY IT EXISTS: the two cast entry points (`InputSurface`'s keyboard branch
 * and `ActionBar`'s click-to-cast) are near-duplicate blocks that have already
 * drifted apart once — that drift is what left a comment in `ActionBar`
 * claiming a rejected click stayed silent when it never did. Both now call
 * THIS, so a readiness rule can never be true in one entry point and false in
 * the other. #787's out-of-resource notice consumes it rather than re-deriving
 * the same comparison a third time.
 *
 * MIRRORS THE SIM, EXACTLY: the resource arm is `@pit/sim`'s `cast.ts` Step 5b,
 * character for character —
 *   `caster.resource !== null && ability.cost > caster.resource.current`
 * A resource-LESS champion carries `resource === null` and 0-cost abilities
 * (crossValidate pins that), so it can never be reported as blocked. This is a
 * client-side PREDICTION for instant feedback (a server round-trip would make
 * the cue lag by RTT); the sim stays authoritative and the caller still sends.
 *
 * REASON-KEYED, ONE ARM SHIPPED: `CastBlockReason` is a closed union with a
 * single member today. Out-of-range (#352), school-lock (`cast.ts` Step 5c) and
 * invalid-target are the intended next arms — each adds a member here plus a
 * copy line in `CastErrorNotice`, and every consumer picks them up for free.
 *
 * Deliberately NOT here: cooldown/charges/GCD. The action bar already renders
 * those continuously (sweep + numeric readout + charge count), so they are not
 * silent failures — they need no error channel, and adding them would make the
 * notice fire constantly during normal play.
 */

import type { ChampionId, ResourceId, WireSimState } from "@pit/protocol";
import type { Dispatch } from "react";
import type { Action } from "../store/types.js";
import { abilityFactsForId } from "../champions/abilityFacts.js";
import { resolveDisplayAbilityMeta } from "../champions/abilityCatalog.js";
import { resourceCeilings } from "../champions/resourceLabels.js";
import { ownEntity } from "../ui/hud-util.js";

/** Why a locally-predicted cast would fail. Closed union — see the module doc
 *  comment for the intended future arms. */
export type CastBlockReason = "insufficientResource";

/** A resolved block, carrying whatever its reason needs to be worded. */
export interface CastBlock {
  readonly reason: CastBlockReason;
  /** The pool that is short (`"energy"`/`"mana"`/`"rage"`), or `null` for an
   *  unmapped/unknown pool — the notice falls back to generic copy. */
  readonly resourceId: ResourceId | null;
}

/** Predicate result. `castable` carries no payload by design: consumers must
 *  not branch on "why is it fine". */
export type Castability =
  | { readonly kind: "castable" }
  | { readonly kind: "blocked"; readonly block: CastBlock };

const CASTABLE: Castability = { kind: "castable" };

/** Everything the predicate reads. All four are already in hand at both cast
 *  entry points. */
export interface CastabilityInput {
  /** Latest sampled snapshot (`null` pre-connect ⇒ nothing to judge). */
  readonly snapshot: WireSimState | null;
  readonly ownSeat: number | null;
  readonly championId: ChampionId;
  /** The CANONICAL ability slot (already resolved from the visual bar
   *  position by `resolveVisualSlotToCanonical` / `resolveActionSlot`). */
  readonly slot: number;
}

/**
 * Can the local player cast `slot` right now? Pure — no store, no DOM, no
 * timers. Unknown/absent inputs resolve to `castable`: this predicate exists to
 * explain a CERTAIN failure, never to guess at one (a false "not enough mana"
 * on a stale snapshot would be worse than silence).
 */
export function castability(input: CastabilityInput): Castability {
  const me = ownEntity(input.snapshot, input.ownSeat);
  if (me === null) return CASTABLE;
  // #76: the ability actually cast from this slot right now (e.g. Backstab →
  // Ambush while stealthed) — the same conditional resolution `cast.ts` does
  // before reading `ability.cost`, and the same one the action-bar icon shows.
  const abilityId = resolveDisplayAbilityMeta(
    input.championId,
    input.slot,
    me.auras,
    input.snapshot?.tick ?? 0,
  )?.id;
  if (abilityId === undefined) return CASTABLE;
  const cost = abilityFactsForId(abilityId)?.cost;
  if (cost === undefined) return CASTABLE;
  // `cast.ts` Step 5b, verbatim: a resource-less caster is never blocked.
  if (me.resource !== null && cost > me.resource) {
    return {
      kind: "blocked",
      block: { reason: "insufficientResource", resourceId: resourceCeilings(input.championId).resourceId },
    };
  }
  return CASTABLE;
}

/**
 * The one-line bridge both cast entry points use: evaluate `castability` and,
 * when blocked, push the reason onto the store's cast-error channel (#787).
 * A castable press dispatches nothing.
 *
 * Deliberately does NOT gate the send — the caller sends either way. The sim is
 * authoritative, this is only a prediction, and swallowing input on a
 * mispredict would be a gameplay regression.
 */
export function reportCastError(dispatch: Dispatch<Action>, input: CastabilityInput): void {
  const verdict = castability(input);
  if (verdict.kind === "castable") return;
  dispatch({ type: "castError.add", block: verdict.block, ts: Date.now() });
}
